"use client";

import { ChevronsDownIcon, SquarePlusIcon } from "lucide-react";

import { AskTwinMark } from "@/components/app/sessions/ask-twin-view";
import Link from "next/link";
import { useMemo, type ReactNode } from "react";
import {
  ChannelNavigationRow,
  SessionNavigationRow,
  type SessionNavigationImmediateAction,
} from "./workspace-navigation-row";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type WorkspaceChannel,
  type WorkspaceSession,
} from "@/lib/mlx-workspace-client";
import { useWorkspaceSessionSearch } from "@/lib/workspace-session-search";
import { type WorkspaceSessionOwnerDisplay } from "@/lib/workspace-session-owner";
import type { ChannelGroupListing as WorkspaceChannelGroup } from "@mlx/contracts";
import {
  channelMatchesNavigationQuery,
  channelNavigationSections,
  liveSessionSearchResults,
  mergePinnedChannels,
  mergePinnedSessions,
} from "./workspace-navigation-model";
import {
  ChannelGroupSection,
  type ChannelGroupSectionActions,
} from "./workspace-navigation-channel-group-section";
import { UNGROUPED_CHANNELS_LABEL } from "@/lib/ungrouped-channels";

export type NavigationTab = "sessions" | "channels";

export function NavigationCreateAction(props: {
  newSessionHref?: string;
  onNewChannel?: () => void;
  onNewSession?: () => void;
  tab: NavigationTab;
}) {
  const label = props.tab === "sessions" ? "New session" : "New channel";
  const isSession = props.tab === "sessions";
  const icon = isSession ? (
    <AskTwinMark />
  ) : (
    <SquarePlusIcon className="size-3.5" strokeWidth={1.75} />
  );
  const triggerClass = isSession
    ? "translate-x-1 text-primary hover:bg-primary/10 hover:text-link-hover"
    : "translate-x-1 text-muted-foreground hover:text-foreground";
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          isSession ? (
            <Button
              aria-label={label}
              className={triggerClass}
              size="icon-xs"
              variant="ghost"
              render={
                <Link
                  href={props.newSessionHref ?? "/workspace/sessions/new"}
                  onClick={(event) => {
                    if (!props.onNewSession) return;
                    if (
                      event.metaKey ||
                      event.ctrlKey ||
                      event.altKey ||
                      event.shiftKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    props.onNewSession?.();
                  }}
                />
              }
              nativeButton={false}
            >
              {icon}
            </Button>
          ) : (
            <Button
              aria-label={label}
              className={triggerClass}
              onClick={props.onNewChannel}
              size="icon-xs"
              variant="ghost"
            >
              {icon}
            </Button>
          )
        }
      />
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

type SessionGroups = ReturnType<typeof mergePinnedSessions>;

export function SessionNavigationList(props: {
  activeId: string | null;
  groups: SessionGroups;
  hiddenIds: ReadonlySet<string>;
  limit: number;
  pendingId: string | null;
  pinPending: (type: "session" | "channel", id: string) => boolean;
  readPending: (id: string) => boolean;
  readStates: ReadonlyMap<string, { unread: boolean }>;
  pinnedIds: ReadonlySet<string>;
  query: string;
  search: ReturnType<typeof useWorkspaceSessionSearch>;
  sessions: WorkspaceSession[];
  createAction: ReactNode;
  ownerForSession: (session: WorkspaceSession) => WorkspaceSessionOwnerDisplay;
  onAction: (
    session: WorkspaceSession,
    action: SessionNavigationImmediateAction,
  ) => void;
  onArchive: (session: WorkspaceSession) => void;
  onDelete: (session: WorkspaceSession) => void;
  onPin: (id: string) => void;
  onRename: (session: WorkspaceSession) => void;
  onShowMore: () => void;
  onReadChange: (id: string, unread: boolean) => void;
}) {
  const searchActive = Boolean(props.query.trim());
  const results = useMemo(
    () =>
      liveSessionSearchResults(props.search.results, props.sessions).filter(
        (result) => !props.hiddenIds.has(result.session.id),
      ),
    [props.hiddenIds, props.search.results, props.sessions],
  );
  const browseRows = [
    ...props.groups.pinned,
    ...props.groups.active,
    ...props.groups.recent,
  ];
  const visibleIds = new Set(
    browseRows.slice(0, props.limit).map((session) => session.id),
  );
  const visibleActive = props.groups.active.filter((item) =>
    visibleIds.has(item.id),
  );
  const row = (session: WorkspaceSession, snippet?: string) => {
    const unread = props.readStates.get(session.id)?.unread === true;
    return (
      <SessionNavigationRow
        key={session.id}
        active={props.activeId === session.id}
        pending={
          props.pendingId === session.id ||
          props.pinPending("session", session.id) ||
          props.readPending(session.id)
        }
        pinned={props.pinnedIds.has(session.id)}
        unread={unread}
        session={session}
        provenanceActor={props.ownerForSession(session)}
        snippet={snippet}
        onArchive={() => props.onArchive(session)}
        onDelete={() => props.onDelete(session)}
        onPinChange={() => props.onPin(session.id)}
        onReadChange={() => props.onReadChange(session.id, !unread)}
        onRename={() => props.onRename(session)}
        onSessionAction={(action) => props.onAction(session, action)}
      />
    );
  };
  if (searchActive)
    return (
      <>
        <NavigationSection action={props.createAction} label="Search results">
          {results.map((result) =>
            row(
              result.session,
              result.matchKind === "title" ? undefined : result.snippet,
            ),
          )}
        </NavigationSection>
        <NavigationListState
          loading={props.search.isLoading}
          error={props.search.error}
          empty={!props.search.isLoading && results.length === 0}
        />
        <ShowMore
          visible={props.search.hasMore}
          loading={props.search.isLoadingMore}
          onClick={() => void props.search.loadMore()}
        />
      </>
    );
  return (
    <>
      <NavigationSection label="Pinned">
        {props.groups.pinned
          .filter((item) => visibleIds.has(item.id))
          .map((item) => row(item))}
      </NavigationSection>
      <NavigationSection
        action={props.createAction}
        emptyMessage="No sessions running"
        label="Active"
      >
        {visibleActive.map((item) => row(item))}
      </NavigationSection>
      <NavigationSection label="Recent">
        {props.groups.recent
          .filter((item) => visibleIds.has(item.id))
          .map((item) => row(item))}
      </NavigationSection>
      <ShowMore
        visible={browseRows.length > props.limit}
        onClick={props.onShowMore}
      />
    </>
  );
}

type ChannelGroups = ReturnType<typeof mergePinnedChannels>;

export function ChannelNavigationList(props: {
  activeId: string | null;
  groups: ChannelGroups;
  /** The organisation's channel groups, when the deployment shows them. */
  organisationGroups?: {
    groups: readonly WorkspaceChannelGroup[];
    actions: ChannelGroupSectionActions;
  };
  limit: number;
  pendingId: string | null;
  pinPending: (type: "session" | "channel", id: string) => boolean;
  pinnedIds: ReadonlySet<string>;
  query: string;
  runningIds: ReadonlySet<string>;
  createAction: ReactNode;
  ownerForChannel: (
    channel: WorkspaceChannel,
  ) => WorkspaceSessionOwnerDisplay | null;
  /** Marks external channels; a guest's channels are all external, so not for a guest. */
  showExternal?: boolean;
  /** Absent for a guest, who cannot archive or rename channels. */
  onArchive?: (channel: WorkspaceChannel) => void;
  onMoveToGroup?: (channel: WorkspaceChannel) => void;
  onPin: (id: string) => void;
  onRename?: (channel: WorkspaceChannel) => void;
  onShowMore: () => void;
}) {
  const all = [...props.groups.pinned, ...props.groups.channels].filter(
    (channel) => channelMatchesNavigationQuery(channel, props.query),
  );
  const visible = all.slice(0, props.limit);
  const { onArchive: archive, onMoveToGroup: moveToGroup, onRename: rename } = props;
  const row = (channel: WorkspaceChannel) => (
    <ChannelNavigationRow
      key={channel.id}
      active={props.activeId === channel.id}
      channel={channel}
      provenanceActor={props.ownerForChannel(channel)}
      pending={
        props.pendingId === channel.id ||
        props.pinPending("channel", channel.id)
      }
      pinned={props.pinnedIds.has(channel.id)}
      running={props.runningIds.has(channel.id)}
      external={props.showExternal === true && channel.audience === "external"}
      {...(archive ? { onArchive: () => archive(channel) } : {})}
      {...(moveToGroup ? { onMoveToGroup: () => moveToGroup(channel) } : {})}
      onPinChange={() => props.onPin(channel.id)}
      {...(rename ? { onRename: () => rename(channel) } : {})}
    />
  );
  if (props.query.trim())
    return (
      <>
        <NavigationSection action={props.createAction} label="Search results">
          {visible.map(row)}
        </NavigationSection>
        <NavigationListState empty={visible.length === 0} />
      </>
    );
  const organisationGroups = props.organisationGroups;
  if (organisationGroups && organisationGroups.groups.length > 0) {
    // Groups show every channel filed in them; the limit pages the ungrouped rest.
    const sections = channelNavigationSections(props.groups.channels, organisationGroups.groups);
    return (
      <>
        <NavigationSection label="Pinned">{props.groups.pinned.map(row)}</NavigationSection>
        {sections.grouped.map(({ group, channels }, index) => (
          <ChannelGroupSection
            key={group.id}
            actions={organisationGroups.actions}
            count={sections.grouped.length}
            group={group}
            headerClassName={NAVIGATION_SECTION_HEADER_CLASS}
            index={index}
          >
            {channels.map(row)}
          </ChannelGroupSection>
        ))}
        <NavigationSection action={props.createAction} label={UNGROUPED_CHANNELS_LABEL}>
          {sections.ungrouped.slice(0, props.limit).map(row)}
        </NavigationSection>
        <ShowMore visible={sections.ungrouped.length > props.limit} onClick={props.onShowMore} />
      </>
    );
  }
  const visibleIds = new Set(visible.map((channel) => channel.id));
  return (
    <>
      <NavigationSection label="Pinned">
        {props.groups.pinned.filter((item) => visibleIds.has(item.id)).map(row)}
      </NavigationSection>
      <NavigationSection action={props.createAction} label="Channels">
        {props.groups.channels
          .filter((item) => visibleIds.has(item.id))
          .map(row)}
      </NavigationSection>
      <ShowMore visible={all.length > props.limit} onClick={props.onShowMore} />
    </>
  );
}

const NAVIGATION_SECTION_HEADER_CLASS =
  "sticky top-0 z-10 flex h-7 items-center bg-sidebar-panel/95 px-3 text-xs font-medium text-muted-foreground backdrop-blur";

function NavigationSection({
  action,
  children,
  emptyMessage,
  label,
}: {
  action?: ReactNode;
  children: ReactNode;
  emptyMessage?: string;
  label: string;
}) {
  const hasRows = Array.isArray(children)
    ? children.length > 0
    : Boolean(children);
  if (!hasRows && !action) return null;
  return (
    <section className="pb-0.5">
      <div className={NAVIGATION_SECTION_HEADER_CLASS}>
        <h2>{label}</h2>
        {action ? <div className="ml-auto -mr-1">{action}</div> : null}
      </div>
      {hasRows ? (
        children
      ) : emptyMessage ? (
        <p className="px-3 pb-1.5 text-2xs leading-5 text-tertiary-foreground">
          {emptyMessage}
        </p>
      ) : null}
    </section>
  );
}

function NavigationListState(props: {
  empty?: boolean;
  error?: Error | null;
  loading?: boolean;
}) {
  if (props.loading)
    return (
      <p className="px-3 py-6 text-center text-xs text-muted-foreground">
        Searching…
      </p>
    );
  if (props.error)
    return (
      <p className="px-3 py-6 text-center text-xs text-destructive">
        {props.error.message}
      </p>
    );
  if (props.empty)
    return (
      <p className="px-3 py-6 text-center text-xs text-muted-foreground">
        No matches
      </p>
    );
  return null;
}

function ShowMore(props: {
  loading?: boolean;
  onClick: () => void;
  visible: boolean;
}) {
  if (!props.visible) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="mx-2 my-1 w-[calc(100%-1rem)] text-muted-foreground"
      disabled={props.loading}
      onClick={props.onClick}
    >
      <ChevronsDownIcon className="size-3.5" />
      {props.loading ? "Loading…" : "Show 50 more"}
    </Button>
  );
}
